import { useState, useEffect, useRef } from 'react'
import Modal from './Modal'
import { Select, Switch } from './ui'
import { hideMedia, restoreMedia, updateMedia, getApiErrorMessage } from '../api'
import { useI18n } from '../i18n'
import { useToast } from '../contexts/ToastContext'
import { MEDIA_CATEGORY_VALUES } from '../pages/ModelDetail/utils'
import MediaTypeLabel from './MediaTypeLabel'
import '../styles/components/media-edit-modal.css'

const BODY_PARTS_VALUES = ['face', 'chest', 'butt', 'pussy', 'legs']

/**
 * Модалка редактирования медиа: is_paid (paid/free), описание, горячесть, части тела.
 * Цена — у группы (MediaGroup), не у медиа.
 */
export default function MediaEditModal({ open, modelId, item, onClose, onSaved }) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [isPaid, setIsPaid] = useState(false)
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [excludedFromAutoUse, setExcludedFromAutoUse] = useState(false)
  const [contentCategory, setContentCategory] = useState('')
  const [hotness, setHotness] = useState('')
  const [bodyParts, setBodyParts] = useState([])
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [confirmVisibility, setConfirmVisibility] = useState(false)
  const [error, setError] = useState('')
  const activeScope = useRef('')
  const scope = open ? `${modelId}:${item?.id}` : ''
  activeScope.current = scope

  useEffect(() => { setConfirmVisibility(false) }, [scope])

  useEffect(() => {
    if (open && item) {
      setIsPaid(!!item.is_paid)
      setPriceMin(item.price_min == null ? '' : String(item.price_min))
      setPriceMax(item.price_max == null ? '' : String(item.price_max))
      setExcludedFromAutoUse(!!item.excluded_from_auto_use)
      setContentCategory(item.content_category || '')
      setHotness(item.hotness != null && item.hotness >= 1 && item.hotness <= 10 ? String(item.hotness) : '')
      const parts = Array.isArray(item.body_parts) ? item.body_parts : []
      setBodyParts(BODY_PARTS_VALUES.filter((v) => parts.includes(v)))
      setDescription(item.description ?? '')
      setError('')
    }
  }, [
    open,
    item?.id,
    item?.is_paid,
    item?.price_min,
    item?.price_max,
    item?.excluded_from_auto_use,
    item?.content_category,
    item?.hotness,
    item?.body_parts,
    item?.description,
  ])

  function toggleBodyPart(value) {
    setBodyParts((prev) => {
      if (prev.includes(value)) return prev.filter((x) => x !== value)
      return [...prev, value]
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!modelId || !item?.id || saving || savingVisibility || confirmVisibility) return
    setSaving(true)
    setError('')
    try {
      const updated = await updateMedia(modelId, item.id, {
        is_paid: isPaid,
        price_min: isPaid ? priceMin : null,
        price_max: isPaid ? priceMax : null,
        excluded_from_auto_use: excludedFromAutoUse,
        content_category: contentCategory || null,
        hotness: hotness === '' ? null : (parseInt(hotness, 10) >= 1 && parseInt(hotness, 10) <= 10 ? parseInt(hotness, 10) : null),
        body_parts: bodyParts.length ? bodyParts : null,
        description: description.trim() || null,
      })
      if (activeScope.current !== scope) return
      onSaved?.({ ...item, ...updated })
      onClose?.()
      addToast(t('toast.media.saved'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('media.edit.modal.error'))
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  async function handleVisibilityChange() {
    if (!modelId || !item?.id || savingVisibility) return
    if (!confirmVisibility) {
      setConfirmVisibility(true)
      return
    }
    setSavingVisibility(true)
    setError('')
    try {
      const updated = item.is_hidden
        ? await restoreMedia(modelId, item.id)
        : await hideMedia(modelId, item.id)
      if (activeScope.current !== scope) return
      if (typeof updated?.is_hidden !== 'boolean' || updated.is_hidden !== !item.is_hidden) {
        throw new Error(t('media.library.error'))
      }
      onSaved?.({ ...item, ...updated, _visibility_changed: true })
      onClose?.()
      addToast(t(item.is_hidden ? 'media.library.restored' : 'media.library.removed'), 'success')
    } catch (err) {
      if (activeScope.current === scope) setError(getApiErrorMessage(err, t('media.library.error')))
    } finally {
      setSavingVisibility(false)
    }
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      title={t('media.edit.modal.title')}
      titleId="media-edit-modal-title"
      onClose={() => { if (!saving && !savingVisibility) onClose?.() }}
      footer={
        <div className="modal-actions">
          <button
            type="button"
            className={item?.is_hidden ? 'modal-btn modal-btn-secondary' : 'modal-btn modal-btn-danger'}
            onClick={handleVisibilityChange}
            disabled={saving || savingVisibility || (item?.is_hidden && !!item?.provider_missing_since)}
            title={item?.is_hidden && item?.provider_missing_since
              ? 'Restore this item at the provider, then run a full sync'
              : undefined}
          >
            {savingVisibility ? t('common.saving') : confirmVisibility ? t('media.library.confirm') : t(item?.is_hidden ? 'media.library.restore' : 'media.library.remove')}
          </button>
          <button
            type="button"
            className="modal-btn modal-btn-secondary"
            onClick={() => { if (confirmVisibility) setConfirmVisibility(false); else onClose?.() }}
            disabled={saving || savingVisibility}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="media-edit-form"
            className="modal-btn modal-btn-primary"
            disabled={saving || savingVisibility || confirmVisibility}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      }
    >
      <form id="media-edit-form" className="media-edit-form" onSubmit={handleSubmit}>
        <MediaTypeLabel item={item} />
        {error && <p className="media-edit-error">{error}</p>}
        {confirmVisibility ? (
          <p className="media-edit-visibility-note" role="status">
            {t(item?.is_hidden ? 'media.library.restoreHint' : 'media.library.removeHint')}
          </p>
        ) : null}
        {item?.provider_missing_since ? (
          <p className="media-edit-visibility-note">
            This media is no longer present at the provider and is blocked from automated use.
            Restore it there and run a full sync to make it available again.
          </p>
        ) : item?.is_hidden ? (
          <p className="media-edit-visibility-note">This media is hidden from the library and automated use.</p>
        ) : null}
        <div className="media-edit-form-row">
          <Switch
            checked={isPaid}
            onChange={setIsPaid}
            label={t('media.edit.isPaidLabel')}
            ariaLabel={t('media.edit.isPaidLabel')}
          />
        </div>
        <label className="media-edit-label">
          {t('media.edit.mediaPrice', 'Individual media price ($)')}
          <div className="media-edit-price-inputs">
            <input
              type="number"
              min="0"
              max="200"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className="media-edit-input media-edit-input--price"
              placeholder="Min"
              disabled={!isPaid}
            />
            <span className="media-edit-price-sep">-</span>
            <input
              type="number"
              min="0"
              max="200"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="media-edit-input media-edit-input--price"
              placeholder="Max"
              disabled={!isPaid}
            />
          </div>
          <span className="media-edit-hint">
            {t('media.edit.mediaPriceHint', 'Use this only when this media has its own PPV price. Stage/group price is edited on the group.')}
          </span>
        </label>
        <div className="media-edit-form-row">
          <Switch
            checked={excludedFromAutoUse}
            onChange={setExcludedFromAutoUse}
            label={t('media.edit.excludedFromAutoUse')}
            ariaLabel={t('media.edit.excludedFromAutoUse')}
          />
        </div>
        <label className="media-edit-label media-edit-label--description">
          {t('media.edit.description')}
          <textarea
            className="media-edit-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('media.edit.description.placeholder')}
            rows={3}
          />
        </label>
        <label className="media-edit-label">
          {t('media.edit.category')}
          <Select
            className="media-edit-category-select"
            value={contentCategory}
            placeholder={t('media.edit.category.none')}
            dropdownPosition="down"
            options={MEDIA_CATEGORY_VALUES.map((value) => ({
              value,
              label: value ? t(`media.category.${value}`) : t('media.edit.category.none'),
            }))}
            onChange={(v) => setContentCategory(v)}
          />
        </label>
        <label className="media-edit-label">
          {t('media.edit.hotness')}
          <Select
            className="media-edit-hotness-select"
            value={hotness}
            placeholder={t('media.edit.notSelected')}
            dropdownPosition="down"
            options={[
              { value: '', label: t('media.edit.notSelected') },
              ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
            ]}
            onChange={(v) => setHotness(v)}
          />
        </label>
        <div className="media-edit-body-parts-block">
          <span className="media-edit-label-text">{t('media.edit.bodyParts')}</span>
          <div className="media-edit-body-parts-tags">
            {BODY_PARTS_VALUES.map((value) => {
              const selected = bodyParts.includes(value)
              const labelKey = `media.edit.bodyParts.${value}`
              const label = t(labelKey)
              return (
                <button
                  key={value}
                  type="button"
                  className={`media-edit-tag ${selected ? 'media-edit-tag-selected' : 'media-edit-tag-add'}`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleBodyPart(value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleBodyPart(value)
                    }
                  }}
                  aria-label={
                    selected
                      ? t('media.edit.bodyParts.remove', { label })
                      : t('media.edit.bodyParts.add', { label })
                  }
                  aria-pressed={selected}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      </form>
    </Modal>
  )
}
